import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  AssignmentForUser,
  DismissedHints,
  HintId,
  ItemForUser,
  LoginMethod,
  PermissionKey,
  PublicUser,
  SecretPayload,
  SessionInfo,
} from '@shared/types';
import { api, ApiError, type AssignmentInput, type ItemInput } from './api';
import { EMPTY_REPORT, findDuplicates, type DuplicateReport } from './duplicates';
import {
  forgetKey,
  recallKey,
  rememberKey,
  savedChoice,
  saveChoice,
  type RememberChoice,
} from './keystore';
import {
  changePassphrase,
  createVaultKey,
  decryptSecret,
  encryptSecret,
  generateItemKey,
  unlockVaultKey,
  unwrapItemKey,
  wrapForAll,
  wrapItemKey,
} from './crypto';

export type VaultStatus =
  | 'loading'
  | 'anonymous'
  | 'needs-setup'
  | 'locked'
  | 'ready'
  | 'error';

/** Nach so vielen Minuten ohne Aktivitaet wird LOCKER automatisch gesperrt. */
const AUTO_LOCK_MINUTES = 15;

interface VaultContextValue {
  status: VaultStatus;
  error: string | null;
  session: SessionInfo | null;
  users: PublicUser[];
  items: ItemForUser[];
  me: PublicUser | null;
  isAdmin: boolean;
  /** Ob die angemeldete Person ein bestimmtes Recht hat. */
  can: (key: PermissionKey) => boolean;

  reload: () => Promise<void>;
  setupKey: (passphrase: string) => Promise<void>;
  unlock: (passphrase: string, remember?: RememberChoice) => Promise<void>;
  /** Wie lange der Schlüssel auf diesem Gerät gemerkt wird. */
  rememberChoice: RememberChoice;
  setRememberChoice: (choice: RememberChoice) => Promise<void>;
  lock: () => void;
  changeMasterPassphrase: (oldPass: string, newPass: string) => Promise<void>;
  resetKey: () => Promise<number>;
  logout: () => Promise<void>;
  /** Zurückgestellte Hinweise der angemeldeten Person. */
  dismissedHints: DismissedHints;
  setHint: (hintId: HintId, until?: string | null) => Promise<void>;

  readSecret: (item: ItemForUser) => Promise<SecretPayload>;
  /** Mehrfach benutzte Passwörter. Wird nach dem Entsperren im Hintergrund ermittelt. */
  duplicates: DuplicateReport;
  duplicatesPending: boolean;
  readAssignmentSecret: (item: ItemForUser, assignment: AssignmentForUser) => Promise<SecretPayload>;
  saveAssignment: (args: {
    item: ItemForUser;
    assignmentId: string | null;
    label: string;
    assigneeUids: string[];
    maxUsers: number | null;
    /** true: das ganze Team darf lesen. */
    openToAll: boolean;
    device: string;
    loginMethod: LoginMethod;
    /** null entfernt ein bestehendes Geheimnis. */
    secret: SecretPayload | null;
  }) => Promise<void>;
  removeAssignment: (item: ItemForUser, assignmentId: string) => Promise<void>;
  saveItem: (args: {
    id: string | null;
    meta: ItemInput;
    secret: SecretPayload;
    grantUids: string[];
    rotated: boolean;
  }) => Promise<ItemForUser>;
  shareItem: (item: ItemForUser, uids: string[]) => Promise<void>;
  revokeAccess: (item: ItemForUser, uid: string) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
  regrantAll: (targetUid: string) => Promise<number>;
}

const VaultContext = createContext<VaultContextValue | null>(null);

export function useVault(): VaultContextValue {
  const ctx = useContext(VaultContext);
  if (!ctx) throw new Error('useVault muss innerhalb von <VaultProvider> benutzt werden.');
  return ctx;
}

export function VaultProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<VaultStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [users, setUsers] = useState<PublicUser[]>([]);
  const [items, setItems] = useState<ItemForUser[]>([]);
  const [duplicates, setDuplicates] = useState<DuplicateReport>(EMPTY_REPORT);
  const [duplicatesPending, setDuplicatesPending] = useState(false);
  const [rememberChoice, setRemember] = useState<RememberChoice>(() => savedChoice());

  /** Nur im Speicher. Ein Reload der Seite verlangt die Passphrase erneut - so soll es sein. */
  const privateKeyRef = useRef<CryptoKey | null>(null);
  const secretCache = useRef(new Map<string, SecretPayload>());

  const lock = useCallback(() => {
    privateKeyRef.current = null;
    secretCache.current.clear();
    // Sperren muss etwas bedeuten: ein gemerkter Schlüssel würde die Sperre
    // beim nächsten Neuladen sonst still wieder aufheben.
    void forgetKey();
    // Der Dublettenbericht entsteht aus entschluesselten Passwoertern und
    // gehoert deshalb genauso hinter das Schloss.
    setDuplicates(EMPTY_REPORT);
    setStatus((prev) => (prev === 'ready' ? 'locked' : prev));
  }, []);

  /**
   * Sucht mehrfach benutzte Passwörter. Laeuft nebenher und blockiert das
   * Laden nicht - bei vielen Eintraegen dauert das Entschluesseln einen Moment.
   */
  const checkDuplicates = useCallback(async (list: ItemForUser[]) => {
    if (!privateKeyRef.current) return;
    setDuplicatesPending(true);
    try {
      const report = await findDuplicates(list, async (item, assignmentId) => {
        try {
          if (assignmentId === null) {
            const payload = await decryptSecret(
              await unwrapItemKey(item.wrappedKey, privateKeyRef.current!),
              item.secret,
            );
            return payload.password || null;
          }
          const assignment = item.assignments.find((a) => a.id === assignmentId);
          if (!assignment?.secret || !assignment.wrappedKey) return null;
          const payload = await decryptSecret(
            await unwrapItemKey(assignment.wrappedKey, privateKeyRef.current!),
            assignment.secret,
          );
          return payload.password || null;
        } catch {
          // Ein einzelner unlesbarer Eintrag darf den ganzen Bericht nicht kippen.
          return null;
        }
      });
      setDuplicates(report);
    } finally {
      setDuplicatesPending(false);
    }
  }, []);

  const loadData = useCallback(async () => {
    const [userList, itemList] = await Promise.all([api.users(), api.items()]);
    setUsers(userList);
    setItems(itemList);
    void checkDuplicates(itemList);
  }, [checkDuplicates]);

  const reload = useCallback(async () => {
    try {
      const info = await api.session();
      setSession(info);
      if (!info.keyBundle) {
        setStatus('needs-setup');
        return;
      }
      if (!privateKeyRef.current) {
        // Liegt auf diesem Gerät ein gültiger Schlüssel, geht es ohne Passphrase weiter.
        const gemerkt = await recallKey(info.user.uid);
        if (!gemerkt) {
          setStatus('locked');
          return;
        }
        privateKeyRef.current = gemerkt;
      }
      await loadData();
      setStatus('ready');
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        setSession(null);
        setStatus('anonymous');
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
      setStatus('error');
    }
  }, [loadData]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Automatisches Sperren bei Inaktivitaet.
  useEffect(() => {
    if (status !== 'ready') return undefined;
    let timer = window.setTimeout(lock, AUTO_LOCK_MINUTES * 60_000);
    const bump = (): void => {
      window.clearTimeout(timer);
      timer = window.setTimeout(lock, AUTO_LOCK_MINUTES * 60_000);
    };
    const events: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'focus'];
    events.forEach((e) => window.addEventListener(e, bump));
    return () => {
      window.clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, bump));
    };
  }, [status, lock]);

  const setupKey = useCallback(
    async (passphrase: string, remember: RememberChoice = rememberChoice) => {
      const { bundle, privateKey } = await createVaultKey(passphrase);
      await api.createVaultKey(bundle);
      privateKeyRef.current = privateKey;
      saveChoice(remember);
      setRemember(remember);
      const info = await api.session();
      await rememberKey(privateKey, info.user.uid, remember);
      await reload();
    },
    [reload, rememberChoice],
  );

  const unlock = useCallback(
    async (passphrase: string, remember: RememberChoice = rememberChoice) => {
      const bundle = session?.keyBundle;
      if (!bundle) throw new Error('Kein Schlüssel hinterlegt.');
      const privateKey = await unlockVaultKey(passphrase, bundle);
      privateKeyRef.current = privateKey;
      saveChoice(remember);
      setRemember(remember);
      if (session?.user.uid) await rememberKey(privateKey, session.user.uid, remember);
      await loadData();
      setStatus('ready');
    },
    [session, loadData, rememberChoice],
  );

  /** Die Wahl nachträglich ändern - etwa in den Einstellungen. */
  const setRememberChoice = useCallback(
    async (choice: RememberChoice) => {
      saveChoice(choice);
      setRemember(choice);
      const key = privateKeyRef.current;
      if (choice === 'never' || !key || !session?.user.uid) await forgetKey();
      else await rememberKey(key, session.user.uid, choice);
    },
    [session],
  );

  const changeMasterPassphrase = useCallback(
    async (oldPass: string, newPass: string) => {
      const bundle = session?.keyBundle;
      if (!bundle) throw new Error('Kein Schlüssel hinterlegt.');
      const next = await changePassphrase(oldPass, newPass, bundle);
      await api.rewrapVaultKey(next);
      setSession((prev) => (prev ? { ...prev, keyBundle: next } : prev));
    },
    [session],
  );

  const resetKey = useCallback(async () => {
    const { revoked } = await api.resetVaultKey();
    await forgetKey();
    privateKeyRef.current = null;
    secretCache.current.clear();
    await reload();
    return revoked;
  }, [reload]);

  const setHint = useCallback(async (hintId: HintId, until?: string | null) => {
    const hints = await api.dismissHint(hintId, until);
    setSession((prev) =>
      prev ? { ...prev, user: { ...prev.user, dismissedHints: hints } } : prev,
    );
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    await forgetKey();
    privateKeyRef.current = null;
    secretCache.current.clear();
    setSession(null);
    setStatus('anonymous');
  }, []);

  const itemKeyFor = useCallback(async (item: ItemForUser): Promise<CryptoKey> => {
    const privateKey = privateKeyRef.current;
    if (!privateKey) throw new Error('LOCKER ist gesperrt.');
    if (!item.wrappedKey) {
      throw new Error('Für diesen Eintrag hast du keine Freigabe.');
    }
    return unwrapItemKey(item.wrappedKey, privateKey);
  }, []);

  const readSecret = useCallback(
    async (item: ItemForUser): Promise<SecretPayload> => {
      const cached = secretCache.current.get(item.id);
      if (cached) return cached;
      const key = await itemKeyFor(item);
      const payload = await decryptSecret(key, item.secret);
      secretCache.current.set(item.id, payload);
      return payload;
    },
    [itemKeyFor],
  );

  /** Öffentliche Schlüssel der Empfänger. Wer noch keine Passphrase gesetzt hat, fällt raus. */
  const recipientsFor = useCallback(
    (uids: string[], options: { skipWithoutKey?: boolean } = {}): { uid: string; publicKey: string }[] => {
      const byUid = new Map(users.map((u) => [u.uid, u]));
      const out: { uid: string; publicKey: string }[] = [];
      const skipped: string[] = [];
      for (const uid of uids) {
        const user = byUid.get(uid);
        if (user?.publicKey) out.push({ uid, publicKey: user.publicKey });
        else if (user) skipped.push(user.email);
      }
      // Bei "alle dürfen" wäre ein Abbruch unbrauchbar - dann ließe sich die
      // Freigabe erst erteilen, wenn wirklich jede Person eingerichtet ist.
      // Stattdessen bleiben die Fehlenden außen vor; die Ansicht weist darauf hin.
      if (skipped.length > 0 && !options.skipWithoutKey) {
        throw new Error(
          `Diese Personen haben noch keine Master-Passphrase gesetzt und können nichts empfangen: ${skipped.join(', ')}`,
        );
      }
      return out;
    },
    [users],
  );

  /**
   * Speichert einen Eintrag. Es wird immer ein frischer Item-Key erzeugt -
   * damit sind alte Kopien des Geheimtextes nach jeder Aenderung wertlos.
   */
  const saveItem = useCallback(
    async (args: {
      id: string | null;
      meta: ItemInput;
      secret: SecretPayload;
      grantUids: string[];
      rotated: boolean;
    }): Promise<ItemForUser> => {
      const me = session?.user;
      if (!me) throw new Error('Nicht angemeldet.');

      const uids = [...new Set([me.uid, ...args.grantUids])];
      const recipients = recipientsFor(uids);

      const itemKey = await generateItemKey();
      const blob = await encryptSecret(itemKey, args.secret);
      const access = await wrapForAll(itemKey, recipients);

      const saved = args.id
        ? await api.updateItem(args.id, {
            meta: args.meta,
            secret: blob,
            access,
            rotated: args.rotated,
          })
        : await api.createItem({ meta: args.meta, secret: blob, access });

      secretCache.current.set(saved.id, args.secret);
      await loadData();
      return saved;
    },
    [session, recipientsFor, loadData],
  );

  const readAssignmentSecret = useCallback(
    async (item: ItemForUser, assignment: AssignmentForUser): Promise<SecretPayload> => {
      const privateKey = privateKeyRef.current;
      if (!privateKey) throw new Error('LOCKER ist gesperrt.');
      if (!assignment.secret || !assignment.wrappedKey) {
        throw new Error('Zu diesem Platz ist kein Geheimnis hinterlegt, das du lesen darfst.');
      }
      const cacheKey = `${item.id}/${assignment.id}`;
      const cached = secretCache.current.get(cacheKey);
      if (cached) return cached;

      const key = await unwrapItemKey(assignment.wrappedKey, privateKey);
      const payload = await decryptSecret(key, assignment.secret);
      secretCache.current.set(cacheKey, payload);
      return payload;
    },
    [],
  );

  /**
   * Legt einen Platz an oder aendert ihn. Ein hinterlegtes Geheimnis - das
   * persoenliche Passwort oder die Seriennummer - wird fuer die zugewiesene
   * Person und fuer einen selbst verpackt. Ohne den eigenen Schluessel waere
   * es beim naechsten Bearbeiten nicht mehr lesbar.
   */
  const saveAssignment = useCallback(
    async (args: {
      item: ItemForUser;
      assignmentId: string | null;
      label: string;
      assigneeUids: string[];
      maxUsers: number | null;
      openToAll: boolean;
      device: string;
      loginMethod: LoginMethod;
      secret: SecretPayload | null;
    }): Promise<void> => {
      const me = session?.user;
      if (!me) throw new Error('Nicht angemeldet.');

      const base = {
        label: args.label,
        assigneeUids: args.assigneeUids,
        maxUsers: args.maxUsers,
        openToAll: args.openToAll,
        device: args.device,
        loginMethod: args.loginMethod,
      };

      /**
       * Wer den Platz bisher lesen konnte.
       *
       * Bleibt die Zuweisung gleich, muss das so bleiben: sonst sperrt ein
       * neues Passwort die Verantwortlichen aus ihrem eigenen Vertrag aus -
       * und genau das passiert, wenn jemand sein persönliches Passwort ändert.
       * Wird der Platz dagegen umgezogen, ist das Aussperren gewollt.
       */
      const bisher = args.item.assignments.find((a) => a.id === args.assignmentId);
      const zuweisungGleich =
        bisher !== undefined &&
        [...bisher.assigneeUids].sort().join(',') === [...args.assigneeUids].sort().join(',') &&
        bisher.openToAll === args.openToAll;
      const bisherigeLeser = zuweisungGleich ? bisher.accessUids : [];

      let input: AssignmentInput;
      if (args.secret) {
        // Lesen darf, wer zugewiesen ist - plus man selbst, sonst wäre der
        // Eintrag beim nächsten Bearbeiten nicht mehr zu öffnen.
        const readerUids = args.openToAll
          ? [...new Set([me.uid, ...users.filter((u) => u.status === 'active').map((u) => u.uid)])]
          : [...new Set([me.uid, ...args.assigneeUids, ...bisherigeLeser])];
        const recipients = recipientsFor(readerUids, { skipWithoutKey: args.openToAll });
        const key = await generateItemKey();
        input = {
          ...base,
          secret: await encryptSecret(key, args.secret),
          access: await wrapForAll(key, recipients),
        };
      } else {
        input = { ...base, secret: null, access: {} };
      }

      if (args.assignmentId) {
        secretCache.current.delete(`${args.item.id}/${args.assignmentId}`);
        await api.updateAssignment(args.item.id, args.assignmentId, input);
      } else {
        await api.addAssignment(args.item.id, input);
      }
      await loadData();
    },
    [session, users, recipientsFor, loadData],
  );

  const removeAssignment = useCallback(
    async (item: ItemForUser, assignmentId: string) => {
      await api.deleteAssignment(item.id, assignmentId);
      secretCache.current.delete(`${item.id}/${assignmentId}`);
      await loadData();
    },
    [loadData],
  );

  const shareItem = useCallback(
    async (item: ItemForUser, uids: string[]) => {
      const recipients = recipientsFor(uids.filter((u) => !item.accessUids.includes(u)));
      if (recipients.length === 0) return;
      const itemKey = await itemKeyFor(item);
      const grants = await wrapForAll(itemKey, recipients);
      await api.addGrants(item.id, grants);
      await loadData();
    },
    [recipientsFor, itemKeyFor, loadData],
  );

  const revokeAccess = useCallback(
    async (item: ItemForUser, uid: string) => {
      await api.revokeGrant(item.id, uid);
      await loadData();
    },
    [loadData],
  );

  const deleteItem = useCallback(
    async (id: string) => {
      await api.deleteItem(id);
      secretCache.current.delete(id);
      await loadData();
    },
    [loadData],
  );

  /** Gibt einer Person alle Eintraege zurueck, die ich selbst entschluesseln kann. */
  const regrantAll = useCallback(
    async (targetUid: string) => {
      const target = users.find((u) => u.uid === targetUid);
      if (!target?.publicKey) {
        throw new Error('Diese Person hat noch keine neue Master-Passphrase gesetzt.');
      }
      const mine = items.filter((i) => i.wrappedKey && !i.accessUids.includes(targetUid));
      const grants: { itemId: string; wrappedKey: string }[] = [];
      for (const item of mine) {
        const itemKey = await itemKeyFor(item);
        grants.push({ itemId: item.id, wrappedKey: await wrapItemKey(itemKey, target.publicKey) });
      }
      const { applied } = await api.regrant(targetUid, grants);
      await loadData();
      return applied;
    },
    [users, items, itemKeyFor, loadData],
  );

  const value = useMemo<VaultContextValue>(
    () => ({
      status,
      error,
      session,
      users,
      items,
      me: session?.user ?? null,
      isAdmin: session?.user.role === 'admin',
      can: (key: PermissionKey) => session?.user.permissions?.[key] === true,
      dismissedHints: session?.user.dismissedHints ?? {},
      setHint,
      reload,
      setupKey,
      unlock,
      rememberChoice,
      setRememberChoice,
      lock,
      changeMasterPassphrase,
      resetKey,
      logout,
      readSecret,
      duplicates,
      duplicatesPending,
      readAssignmentSecret,
      saveAssignment,
      removeAssignment,
      saveItem,
      shareItem,
      revokeAccess,
      deleteItem,
      regrantAll,
    }),
    [
      status,
      error,
      session,
      users,
      items,
      reload,
      setHint,
      setupKey,
      unlock,
      rememberChoice,
      setRememberChoice,
      lock,
      changeMasterPassphrase,
      resetKey,
      logout,
      readSecret,
      duplicates,
      duplicatesPending,
      readAssignmentSecret,
      saveAssignment,
      removeAssignment,
      saveItem,
      shareItem,
      revokeAccess,
      deleteItem,
      regrantAll,
    ],
  );

  return <VaultContext.Provider value={value}>{children}</VaultContext.Provider>;
}
